import type { AllowedResources } from '#layers/access/public';
import type { Filter } from '#layers/data-model/public';
import { AllowedResourcesKinds } from '#layers/access/public';
import { isNonEmptyFilter, mergeAnd, mergeOr } from '#layers/data-model/public';

/**
 * Build an `id` filter from an allowlist and an exclusion list.
 *
 * Combines two id constraints on a single `id` field, which the engine and the local
 * cache matcher both AND together:
 * - `_in` restricts to the assignable ids; when all are allowed, no `_in` is added.
 * - `_nin` excludes ids the caller does not want offered.
 *
 * Returns `undefined` when neither constraint applies, leaving the result unfiltered.
 */
export function buildAssignmentFilter(allowedIds: AllowedResources, excludedIds: string[]): Filter | undefined {
	const idConstraints: Record<string, unknown> = {};

	if (excludedIds.length > 0) idConstraints._nin = excludedIds;
	if (allowedIds.kind === AllowedResourcesKinds.Some) idConstraints._in = [...allowedIds.ids];

	return Object.keys(idConstraints).length > 0 ? { id: idConstraints } : undefined;
}

/**
 * Build the listing-drawer filter for an assignment picker (policy/role), including staged
 * removals so they can be reconnected. Here `excludedIds` are the already-effective ids —
 * assigned or pending — plus anything the block never offers.
 *
 * Composed with `mergeAnd`/`mergeOr` rather than object spreads: the id constraint and the
 * relationship exclusion are independent filters, and spreading them would let one silently
 * overwrite a key of the other.
 */
export function buildAssignmentPickerFilter(
	allowedIds: AllowedResources,
	excludedIds: string[],
	relationshipExclusion: Filter,
	reconnectableIds: string[],
): Filter {
	const idFilter = buildAssignmentFilter(allowedIds, excludedIds);

	// Staged removals are excluded by the relationship (they are still persisted), so they are
	// offered back through an OR branch that selects them by id.
	const eligibility = reconnectableIds.length === 0
		? relationshipExclusion
		: mergeOr(relationshipExclusion, { id: { _in: reconnectableIds } });

	return isNonEmptyFilter(idFilter) ? mergeAnd(idFilter, eligibility) : eligibility;
}
